import { useEffect, useRef } from "react";
import { RowSelectionState } from "@tanstack/react-table";
import { ModelSummaryDto, ModelsControllerListSummariesData } from "@/client";
import { PaginatedResponseWithCountDto } from "@/client/extend";
import { WithSetter } from "../data-table/data-table-context";
import { ModelSelectionTable } from "../model-deployment/model-selection-table";
import { ModelSelectionTableContext } from "../model-deployment/model-selection-table-context";

interface SelectionObserverProps {
  onSelect: (model: ModelSummaryDto) => void;
}

/**
 * Internal component that observes table row selection and syncs to external callback.
 * Must be rendered inside the ModelSelectionTableContext.Provider.
 */
const SelectionObserver = ({ onSelect }: SelectionObserverProps) => {
  const [rowSelection] = ModelSelectionTableContext.useTableRowSelection();
  const queryResult = ModelSelectionTableContext.useTableQueryResult();
  const prevSelectionRef = useRef<string | null>(null);

  useEffect(() => {
    const selectedIds = Object.keys(rowSelection).filter(
      (key) => (rowSelection as RowSelectionState)[key],
    );
    const selectedId = selectedIds[0] ?? null;

    if (selectedId !== prevSelectionRef.current && selectedId !== null) {
      prevSelectionRef.current = selectedId;

      const data = queryResult.data as
        | PaginatedResponseWithCountDto<ModelSummaryDto>
        | undefined;
      const models = data?.results ?? [];
      const selectedModel = models.find(
        (model) => String(model.id) === selectedId,
      );

      if (selectedModel) {
        onSelect(selectedModel);
      }
    } else if (selectedId === null) {
      prevSelectionRef.current = null;
    }
  }, [rowSelection, queryResult.data, onSelect]);

  return null;
};

export interface ModelSelectionTableWithSelectionProps {
  onSelect: (model: ModelSummaryDto) => void;
  queryArgumentState: WithSetter<ModelsControllerListSummariesData>;
  /** Extra control for the table's filters row. */
  topExtra?: React.ReactNode;
}

/**
 * Model selection table with single-select behavior.
 * Wraps ModelSelectionTable with a SelectionObserver that syncs row selection to the onSelect callback.
 */
export const ModelSelectionTableWithSelection = ({
  onSelect,
  queryArgumentState,
  topExtra,
}: ModelSelectionTableWithSelectionProps) => {
  return (
    <ModelSelectionTableContext.Provider
      queryArgumentState={queryArgumentState}
    >
      <SelectionObserver onSelect={onSelect} />
      <ModelSelectionTable
        context={ModelSelectionTableContext}
        topExtra={topExtra}
      />
    </ModelSelectionTableContext.Provider>
  );
};
